/**
 * Button — shadcn/ui primitive built on `@radix-ui/react-slot`.
 *
 * Lifted from the binding mockup at
 * `reignara_redesign/components/ui/button.tsx` and adapted to import the
 * project's `cn()` utility from `@/lib/utils`. Class strings consume design
 * tokens via Tailwind utilities only (`bg-primary`, `text-primary-foreground`,
 * `bg-background`, `text-accent-foreground`, `border-input`, `ring-ring`,
 * `bg-destructive`, `bg-secondary`, `text-foreground`); no hex / `rgb(` /
 * `hsl(` literals (Requirement 4.5).
 *
 * Feature: meridian-redesign
 * Requirements: 4.3, 4.4, 4.5
 */

import * as React from'react';
import { Slot } from'@radix-ui/react-slot';
import { cva, type VariantProps } from'class-variance-authority';

import { cn } from'@/lib/utils';

const buttonVariants = cva(
"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-input text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 :ring-destructive/40 aria-invalid:border-destructive",
 {
 variants: {
 variant: {
 default:'bg-primary text-primary-foreground hover:bg-primary/90',
 destructive:
'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 :ring-destructive/40',
 outline:
'border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground :bg-input/50',
 secondary:
'bg-secondary text-secondary-foreground hover:bg-secondary/80',
 ghost:
'hover:bg-accent hover:text-accent-foreground :bg-accent/50',
 link:'text-primary underline-offset-4 hover:underline',
 },
 size: {
 default:'h-9 px-4 py-2 has-[>svg]:px-3',
 sm:'h-8 rounded-input gap-1.5 px-3 has-[>svg]:px-2.5',
 lg:'h-10 rounded-input px-6 has-[>svg]:px-4',
 icon:'size-9',
'icon-sm':'size-8',
'icon-lg':'size-10',
 },
 },
 defaultVariants: {
 variant:'default',
 size:'default',
 },
 },
);

type ButtonProps = React.ComponentProps<'button'> &
 VariantProps<typeof buttonVariants> & {
 asChild?: boolean;
 };

/**
 * `forwardRef` is REQUIRED here, not optional polish.
 *
 * Upstream shadcn dropped `forwardRef` because it targets React 19, where a
 * `ref` arrives as an ordinary prop. This project is on React 18, where a
 * plain function component silently cannot receive one: React drops it and
 * warns "Function components cannot be given refs".
 *
 * Every Radix `Trigger asChild` wrapping this Button depends on that ref.
 * Radix uses the trigger's DOM node as the Popper ANCHOR; with a null anchor
 * `useFloating` never resolves a position, `isPositioned` stays false, and
 * `@radix-ui/react-popper` parks the content at `translate(0, -200%)` —
 * rendered, focusable, and completely off-screen above the viewport.
 *
 * That is the production defect behind "the Bulletin ellipsis menu does
 * nothing": the menu opened every time, just off-screen, so Edit could never
 * be clicked and no `editHeraldPost` mutation was ever sent. It affected
 * `herald/DirectorOptionsMenu` and `community/record/RecordHeader` alike.
 *
 * Note this is invisible to jsdom, which has no layout and ignores
 * transforms — so assert the ref itself (see `button.ref.test.tsx`), never
 * the visual outcome.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
 function Button({ className, variant, size, asChild = false, ...props }, ref) {
 const Comp = asChild ? Slot :'button';

 return (
 <Comp
 ref={ref}
 data-slot="button"
 className={cn(buttonVariants({ variant, size, className }))}
 {...props}
 />
 );
 },
);

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
